
//librerías
import { useCallback, useEffect, useMemo, useState } from "react";
import { Container, ProgressBar, Row, Col, ButtonGroup } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faFilePdf, faDownload, faFileExcel, faFileText } from "@fortawesome/free-solid-svg-icons";

//interfaces
import { ActividadIndex } from "../../../interfaces/ActividadIndexInterface";
//types
//custom hooks
import useFetch from "../../../hooks/useFetch";
import { ColumnDef } from "@tanstack/react-table";
import CustomTable from "../../Formularios/CustomTable/CustomTable";

//componentes
import useDownloadPDFDirect from "../../../hooks/useDownloadPDFDirect";
import useDownloadPDFSinFotos from "../../../hooks/useDownloadPDFSinFotos";
import { PDFPreviewModal } from "../../Common/PDFPreviewModal/PDFPreviewModal";
import { usePDFPreview } from "../../../hooks/usePDFPreview";
import useDownloadMassivePDF from "../../../hooks/useDownloadMassivePDF";
import useDownloadMassivePDFIndividual from "../../../hooks/useDownloadMassivePDFIndividual";
import DescargaMasivaModal from "../DescargaMasiva/DescargaMasivaModal";
import useValidarActividad from "../../../hooks/useValidarActividad";
import ValidacionCell from "./ValidacionCell";

//estilos
const classes = require("./ServiciosTable.module.css").default;

/**
 * ServiciosTable Component
 * @description: Componente que muestra el paginado de los servicios.
 * @date 14/11/2022.
 * @param Props Recibe los servicios en un arreglo, la función changeEtapa que actualiza la
 * etapa del contenido Home, la función changeServicio que actualiza el id del servicio al que se quiere saber su detalle.
 * @returns JSX de la tabla del servicio.
 */

const ServiciosAtendidos = () => {
  const [actividades, setActividades] = useState<ActividadIndex[]>();
  const { sendRequest } = useFetch();

  const [loadingExcel, setLoadingExcel] = useState<boolean>(false);
  const [loadingMassivePDF, setLoadingMassivePDF] = useState<boolean>(false);
  const [showModalExcel, setShowModalExcel] = useState<boolean>(false);
  const [showModalPDF, setShowModalPDF] = useState<boolean>(false);
  const [progressPDF, setProgressPDF] = useState<{ current: number; total: number }>({ current: 0, total: 0 });
  
  //Hook para previsualización de PDF
  const { previewPDF, loading: loadingPDF, showModal: showPDFModal, pdfUrl, closeModal } = usePDFPreview();
  
  //Función para descarga masiva de Excel
  const { descargarPDFsMasivo: descargarExcel } = useDownloadMassivePDF(setLoadingExcel);
  
  //Función para descarga masiva de PDFs
  const { descargarPDFsMasivo: descargarPDFsMasivo } = useDownloadMassivePDFIndividual(
    setLoadingMassivePDF,
    (current, total) => setProgressPDF({ current, total })
  );

  //Hook para validar servicios
  const { validarServicio, desvalidarServicio, loadingId } = useValidarActividad(setActividades);

  //Función que trata los servicios traidos de la BD.
  const transformData = useCallback((variable: any) => {
    setActividades(variable.data);
  }, []);

  //Función que llama todos los servicios paginados
  useEffect(() => {
    sendRequest(
      {
        url: "/actividad?includeAll=true&ultima_etapa[eq]=3",
      },
      transformData
    );
  }, [sendRequest, transformData]);

  const columns = useMemo<ColumnDef<ActividadIndex>[]>(
    () => [
      {
        header: "Órden de servicio",
        accessorKey: "id",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Fecha y hora de la cita",
        accessorKey: "fecha",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Persona que capura",
        accessorFn: (row) => {
          let elemento = row.etapa.find(
            (element) => element.tipo_etapa_id === 1
          );
          if (elemento)
            return `${elemento?.usuario.nombre} ${elemento?.usuario.apellido_paterno}`;
          else return "";
        },
        id: "persona_captura",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Sucursal",
        accessorFn: (row) =>
          row.servicio.sucursal?.nombre ? row.servicio.sucursal.nombre : "",
        id: "Sucursal",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Folio",
        accessorFn: (row) => (row.servicio.folio ? row.servicio.folio : ""),
        id: "Folio",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Técnico",
        accessorFn: (row) =>
          `${row.usuario.nombre} ${row.usuario.apellido_paterno} ${row.usuario.apellido_materno}`,
        id: "tecnico",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Tipo de servicio",
        accessorFn: (row) => row.tipo_servicio.nombre,
        id: "tipo_servicio",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "VIN",
        accessorFn: (row) => (row.unidad.vin ? row.unidad.vin : ""),
        id: "vin",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Placas",
        accessorFn: (row) => (row.unidad.placa ? row.unidad.placa : ""),
        id: "placa",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Acciones",
        cell: (info) => (
          <div className="btn-group" role="group" aria-label="Basic example">
            {/** Boton de despliegue del PDF */}
            <Button
              title="Obtener el PDF de este servicio"
              style={{ backgroundColor: "#5DADE2", borderColor: "white" }}
              disabled={loadingPDF || loadingExcel || loadingMassivePDF}
              onClick={() => previewPDF(info.row.original.id, false)}
            >
              <FontAwesomeIcon icon={faFilePdf as IconProp} />
            </Button>
            {/** Boton de despliegue del PDF sin fotos */}
            <Button
              title="Obtener el PDF sin fotos de este servicio"
              style={{ backgroundColor: "#28A745", borderColor: "white" }}
              disabled={loadingPDF || loadingExcel || loadingMassivePDF}
              onClick={() => previewPDF(info.row.original.id, true)}
            >
              <FontAwesomeIcon icon={faFileText as IconProp} />
            </Button>
          </div>
        ),
        footer: (props) => props.column.id,
      },
      {
        header: "Validación",
        id: "validacion",
        cell: (info) => (
          <ValidacionCell
            idActividad={info.row.original.id}
            validadoPor={info.row.original.validado_por}
            validador={info.row.original.validador}
            loading={loadingId === info.row.original.id}
            onValidar={validarServicio}
            onDesvalidar={desvalidarServicio}
          />
        ),
        footer: (props) => props.column.id,
      },
    ],
    [previewPDF, loadingPDF, loadingExcel, loadingMassivePDF, loadingId, validarServicio, desvalidarServicio]
  );

  //mientras se carga el detalle se muestra un spinner
  if (!actividades) {
    return (
      <div className={classes.spinnerContainer}>
        <Spinner animation="grow" variant="info" />
      </div>
    );
  }

  return (
    <Container fluid style={{ paddingBottom: "1rem", paddingLeft: "2rem", paddingRight: "2rem" }}>
      <Row className="mb-3">
        <Col>
          <h1>Servicios Atendidos</h1>
        </Col>
        <Col xs="auto">
          <ButtonGroup>
            <Button
              variant="success"
              onClick={() => setShowModalExcel(true)}
              disabled={loadingPDF || loadingExcel || loadingMassivePDF}
              title="Descargar reporte Excel de servicios atendidos"
            >
              <FontAwesomeIcon icon={faFileExcel as IconProp} className="me-1" />
              {loadingExcel ? "Descargando..." : "Excel"}
            </Button>
            <Button
              variant="danger"
              onClick={() => setShowModalPDF(true)}
              disabled={loadingPDF || loadingExcel || loadingMassivePDF}
              title="Descargar PDFs masivos de servicios atendidos"
            >
              <FontAwesomeIcon icon={faFilePdf as IconProp} className="me-1" />
              {loadingMassivePDF ? "Descargando..." : "PDFs"}
            </Button>
          </ButtonGroup>
        </Col>
      </Row>

      {/* Barra de progreso para PDF individual */}
      {loadingPDF && (
        <ProgressBar
          style={{ marginBottom: "1rem" }}
          label="Creando PDF..."
          animated
          now={100}
          variant="info"
        />
      )}

      {/* Barra de progreso unificada */}
      {loadingPDF && (
        <ProgressBar
          style={{ marginBottom: "1rem" }}
          label="Generando PDF..."
          animated
          now={100}
          variant="success"
        />
      )}

      {/* Barra de progreso para Excel */}
      {loadingExcel && (
        <ProgressBar
          style={{ marginBottom: "1rem" }}
          label="Generando Excel..."
          animated
          now={100}
          variant="success"
        />
      )}

      {/* Barra de progreso para PDFs masivos */}
      {loadingMassivePDF && progressPDF.total > 0 && (
        <ProgressBar
          style={{ marginBottom: "1rem" }}
          label={`Descargando PDFs: ${progressPDF.current}/${progressPDF.total}`}
          now={(progressPDF.current / progressPDF.total) * 100}
          variant="danger"
        />
      )}

      {loadingMassivePDF && progressPDF.total === 0 && (
        <ProgressBar
          style={{ marginBottom: "1rem" }}
          label="Preparando descarga de PDFs..."
          animated
          now={100}
          variant="danger"
        />
      )}

      <CustomTable
        {...{
          data: actividades,
          columns,
          centered: true,
        }}
      />

      {/* Modal para descarga masiva de Excel */}
      <DescargaMasivaModal
        show={showModalExcel}
        onHide={() => setShowModalExcel(false)}
        etapaServicio={3}
        tituloModal="Descargar Reporte Excel - Servicios Atendidos"
        tipoDescarga="excel"
        onDescargar={descargarExcel}
      />

      {/* Modal para descarga masiva de PDFs */}
      <DescargaMasivaModal
        show={showModalPDF}
        onHide={() => setShowModalPDF(false)}
        etapaServicio={3}
        tituloModal="Descargar PDFs Masivos - Servicios Atendidos"
        tipoDescarga="pdf"
        onDescargar={descargarPDFsMasivo}
      />
      
      {/** Modal de previsualización PDF */}
      <PDFPreviewModal
        show={showPDFModal}
        onHide={closeModal}
        pdfUrl={pdfUrl}
        title="Vista previa del servicio"
      />
    </Container>
  );
};

export default ServiciosAtendidos;